import SemanticDatepicker from "react-semantic-ui-datepickers";
import { Dropdown, Form, InputOnChangeData, Label, Segment, Tab } from "semantic-ui-react";
import { FormError } from "../models/FormError";
import ErrorsView from "./ErrorsView";
import OrgUserFilesList from "./OrgUserFilesList";
import { OrgUserFile_AndInput } from "../models/OrgUserFile_AndInput";
import { ActiveLicence } from "../models/ActiveLicence";
import { Licence } from "../models/Licence";
import { AdminRole } from "../models/AdminRole";

export interface FormData {
  firstName: string;
  lastName: string;
  adminRoleId: string | null;
  phoneNumber: string;
  CatSerialNumber: string;
  CatCalibrationDate: Date;
  CatCalibrationDueDate: Date;
  GennySerialNumber: string;
  GennyCalibrationDate: Date;
  GennyCalibrationDueDate: Date;
  licenceId: string | null;
}

interface Props {
  formData: FormData;
  formErrors: FormError;
  errors: string[] | undefined;
  onChange: (formData: FormData) => void;
  onAddOrgUserFile: () => void;
  onDeleteOrgUserFile: (orgUserFile: OrgUserFile_AndInput) => void;
  orgUserFiles?: OrgUserFile_AndInput[];
  activeLicences?: ActiveLicence[] | null;
  licence?: Licence | null;
  licenceSelectorDisabled?: boolean;
  adminRoles?: AdminRole[];
  adminRoleSelectorDisabled?: boolean;
}

const OrgUserEditForm = ({
  formData,
  onChange,
  formErrors,
  errors,
  orgUserFiles,
  onAddOrgUserFile,
  onDeleteOrgUserFile,
  activeLicences,
  licence,
  licenceSelectorDisabled = false,
  adminRoles,
  adminRoleSelectorDisabled = false,
}: Props) => {
  const handleChange = (e: any, { name, value }: InputOnChangeData) => {
    onChange({ ...formData, [name]: value });
  };

  const onCatCalibrationDateChange = (e: any, data: any) => {
    formData.CatCalibrationDate = data.value;
  };

  const onGennyCalibrationDateChange = (e: any, data: any) => {
    formData.GennyCalibrationDate = data.value;
  };

  const onCatCalibrationDueDateChange = (e: any, data: any) => {
    formData.CatCalibrationDueDate = data.value;
  };

  const onGennyCalibrationDueDateChange = (e: any, data: any) => {
    formData.GennyCalibrationDueDate = data.value;
  };

  const panes = [
    {
      menuItem: "CAT and Genny",
      render: () => (
        <Tab.Pane>
          <Form>
            <Form.Group widths="equal">
              <Form.Field>
                <label>CAT serial number</label>
                <Form.Input name="CatSerialNumber" value={formData.CatSerialNumber ?? ""} onChange={handleChange} />
              </Form.Field>
              <SemanticDatepicker
                label="CAT calibration date"
                value={formData.CatCalibrationDate}
                locale="en-US"
                onChange={onCatCalibrationDateChange}
                type="basic"
              />
              <SemanticDatepicker
                label="CAT calibration due date"
                value={formData.CatCalibrationDueDate}
                locale="en-US"
                onChange={onCatCalibrationDueDateChange}
                type="basic"
              />
            </Form.Group>

            <Form.Group widths="equal">
              <Form.Field>
                <label>Genny serial number</label>
                <Form.Input name="GennySerialNumber" value={formData.GennySerialNumber ?? ""} onChange={handleChange} />
              </Form.Field>
              <SemanticDatepicker
                label="Genny calibration date"
                value={formData.GennyCalibrationDate}
                locale="en-US"
                onChange={onGennyCalibrationDateChange}
                type="basic"
              />
              <SemanticDatepicker
                label="Genny calibration due date"
                value={formData.GennyCalibrationDueDate}
                locale="en-US"
                onChange={onGennyCalibrationDueDateChange}
                type="basic"
              />
            </Form.Group>
          </Form>
        </Tab.Pane>
      ),
    },
    {
      menuItem: `Files (${orgUserFiles?.length ?? 0})`,
      render: () => (
        <Tab.Pane>
          <div style={{ height: "163px" }}>
            <OrgUserFilesList
              orgUserFiles={orgUserFiles}
              onAddOrgUserFile={onAddOrgUserFile}
              onDeleteOrgUserFile={onDeleteOrgUserFile}
            />
          </div>
        </Tab.Pane>
      ),
    },
  ];

  return (
    <>
      <Form>
        <Form.Group widths="equal">
          <Form.Input
            label="First Name"
            name="firstName"
            value={formData.firstName ?? ""}
            error={formErrors.firstName}
            onChange={handleChange}
            required
          />
          <Form.Input
            label="Last name"
            name="lastName"
            value={formData.lastName ?? ""}
            error={formErrors.lastName}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group widths="equal">
          <Form.Input
            label="Phone Number"
            name="phoneNumber"
            value={formData.phoneNumber ?? ""}
            onChange={handleChange}
          />

          <Form.Field
            error={formErrors.adminRoleId}
            control={Dropdown}
            selection
            search
            name="adminRoleId"
            value={formData.adminRoleId}
            fluid
            label="Admin Role"
            options={[
              { key: 0, text: "No role", value: null },
              ...(adminRoles?.map((r) => ({
                key: r.id,
                text: r.name.replace(/^[^_]*_/, ""),
                value: r.id,
              })) ?? []),
            ]}
            onChange={handleChange}
            disabled={adminRoleSelectorDisabled}
          />
        </Form.Group>

        <Segment>
          <Label attached="top">
            Assigned licence: {licence && formData.licenceId ? <>"{licence.productName}"</> : <>not assigned</>}
          </Label>
          <Form.Group widths="equal">
            <Form.Field
              error={formErrors.licenceId}
              control={Dropdown}
              selection
              search
              name="licenceId"
              value={formData.licenceId}
              fluid
              label="Change assigned licence"
              options={[
                { key: 0, text: "No licence", value: null },
                ...(activeLicences?.map((l) => ({
                  key: l.id,
                  text: `${l.productName}, Available slots: ${l.availableUserSlots}/${l.numberOfUsers}`,
                  value: l.id,
                })) ?? []),
              ]}
              onChange={handleChange}
              disabled={licenceSelectorDisabled}
            />
          </Form.Group>
        </Segment>
      </Form>
      <br />
      <Tab panes={panes} />
      <ErrorsView errors={errors} />
    </>
  );
};

export default OrgUserEditForm;
